import { Link } from "react-router-dom";
import { ArrowRight, Store, Wallet, Sparkles, FileCheck, Fingerprint, ShieldCheck, MailCheck } from "lucide-react";

import AutheraLogo from "@/components/AutheraLogo";

const Feature = ({ icon: Icon, title, desc }) => (
  <div className="flex gap-4">
    <span className="mt-0.5 h-9 w-9 shrink-0 rounded-lg bg-blue-50 text-[#0055FF] flex items-center justify-center">
      <Icon size={18} strokeWidth={2.2} />
    </span>
    <div>
      <div className="font-semibold text-gray-900">{title}</div>
      <div className="text-sm text-gray-500 leading-relaxed">{desc}</div>
    </div>
  </div>
);

export default function Landing() {
  return (
    <div data-testid="landing-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Hero */}
      <section className="pt-20 pb-12 lg:pt-28 lg:pb-20 rise-in">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gray-200 bg-white text-xs font-medium text-gray-600 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-[#0055FF] animate-pulse" />
              Digital certificates of authenticity · v0.1 demo
            </div>
            <h1 className="font-display font-extrabold tracking-tight text-gray-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Turn every receipt
              <br />
              into a verifiable{" "}
              <span className="text-[#0055FF]">digital certificate</span>.
            </h1>
            <p className="mt-6 text-lg text-gray-600 max-w-xl leading-relaxed">
              Issue tamper-evident certificates of authenticity for any high-value
              purchase — watches, instruments, collectibles — anchored on-chain
              and verifiable by anyone.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
              <Link
                to="/demo"
                data-testid="cta-try-verification"
                className="inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white px-6 py-3 rounded-lg font-semibold shadow-sm hover:bg-[#0044CC] hover:-translate-y-0.5 transition-all"
              >
                <MailCheck size={18} />
                Try Verification
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/retailer"
                data-testid="cta-retailer"
                className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 border border-gray-200 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 hover:border-gray-300 hover:-translate-y-0.5 transition-all"
              >
                <Store size={18} />
                Enter Retailer Dashboard
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/verify"
                data-testid="cta-verify"
                className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 border border-gray-200 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 hover:border-[#0055FF] hover:text-[#0055FF] hover:-translate-y-0.5 transition-all"
              >
                <ShieldCheck size={18} />
                Verify a Certificate
              </Link>
              <Link
                to="/customer"
                data-testid="cta-customer"
                className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 border border-gray-200 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 hover:border-gray-300 hover:-translate-y-0.5 transition-all"
              >
                <Wallet size={18} />
                View Customer Certificates
              </Link>
            </div>

            <div className="mt-10 grid sm:grid-cols-3 gap-6 max-w-2xl">
              <Feature
                icon={FileCheck}
                title="Upload receipts"
                desc="PDF or JSON. Preview and edit before saving."
              />
              <Feature
                icon={Sparkles}
                title="Issue certificate"
                desc="On-chain proof of authenticity, instantly delivered."
              />
              <Feature
                icon={Fingerprint}
                title="Portable proof"
                desc="Customers and resellers can verify ownership anywhere."
              />
            </div>
          </div>

          {/* Email preview mockup — what customers receive after purchase */}
          <div className="lg:col-span-5">
            <div
              data-testid="hero-email-preview"
              className="relative rounded-2xl border border-gray-200 bg-white shadow-[0_24px_64px_rgba(0,85,255,0.16)] overflow-hidden"
            >
              <div className="h-[3px] bg-gradient-to-r from-transparent via-[#0055FF] to-transparent" />

              {/* Mock email chrome */}
              <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 bg-gray-50/60">
                <div className="flex gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
                </div>
                <div className="ml-3 text-[11px] text-gray-500 truncate">
                  <span className="font-mono">hello@autheraapp.com</span>
                  <span className="mx-1.5 text-gray-300">·</span>
                  Your Authera Digital Certificate
                </div>
              </div>

              {/* Email body */}
              <div className="px-7 pt-7 pb-6">
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#0055FF]">
                  Authera
                </div>
                <h3 className="mt-1.5 font-display font-extrabold text-gray-900 text-2xl tracking-tight">
                  Your Certificate is Ready
                </h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                  Your purchase has been verified and secured on-chain.
                </p>

                {/* Embedded certificate card */}
                <div className="mt-5 rounded-xl border border-gray-200 overflow-hidden bg-white">
                  <div className="relative aspect-[16/9] overflow-hidden bg-gradient-to-br from-[#0a1628] via-[#0f1f3a] to-[#0a1628]">
                    <img
                      src="https://images.unsplash.com/photo-1612817159949-195b6eb9e31a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                      alt="Rolex Submariner Date"
                      className="absolute inset-0 w-full h-full object-cover opacity-95"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/95 border border-white/40 text-[9px] font-bold tracking-wider uppercase text-[#0055FF] px-2 py-1 rounded-md">
                      <ShieldCheck size={10} strokeWidth={2.5} />
                      Verified
                    </div>
                    {/* Authera logo badge (top-right) */}
                    <div className="absolute top-4 right-4 z-10">
                      <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-blue-600 shadow-[0_0_22px_rgba(37,99,235,0.75),0_10px_22px_rgba(0,0,0,0.4)]">
                        <AutheraLogo size={36} glow />
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-3.5 border-t border-gray-100">
                    <div className="text-[10px] font-semibold tracking-wider uppercase text-gray-400">
                      Authera Demo Store
                    </div>
                    <div className="mt-0.5 font-display font-bold text-gray-900 text-[15px] leading-tight">
                      Rolex Submariner Date
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px]">
                      <span className="font-mono text-gray-500">NFT-9AF3B2C10E</span>
                      <span className="font-mono text-[#0055FF]">$11,160.25</span>
                    </div>
                  </div>
                </div>

                {/* QR + CTA */}
                <div className="mt-5 grid grid-cols-[auto,1fr] gap-4 items-center">
                  <div className="h-[88px] w-[88px] rounded-lg border border-gray-200 bg-white p-1.5 flex items-center justify-center">
                    <img
                      src="/api/public/qr/0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef.png"
                      alt="Verify QR"
                      width={80}
                      height={80}
                      className="block"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#0055FF]">
                      Scan to Verify
                    </div>
                    <button
                      type="button"
                      className="mt-2 inline-flex items-center gap-1.5 bg-[#0055FF] text-white text-xs font-semibold px-3.5 py-2 rounded-md shadow-sm pointer-events-none"
                    >
                      View &amp; Verify Certificate
                      <ArrowRight size={12} />
                    </button>
                    <div className="mt-2 text-[10px] font-mono text-gray-400 truncate">
                      app.autheraapp.com/verify/0x123…cdef
                    </div>
                  </div>
                </div>
              </div>

              {/* Email footer */}
              <div className="px-7 py-3 bg-gray-50/70 border-t border-gray-100 text-center">
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-gray-700">
                  Authera
                </div>
                <div className="text-[10px] text-gray-400 mt-0.5">
                  Digital Asset Verification
                </div>
              </div>
            </div>
            <div className="mt-3 text-center text-[11px] text-gray-500">
              Sample of the email every customer receives after purchase.
            </div>
          </div>
        </div>
      </section>

      {/* Site footer */}
      <footer data-testid="site-footer" className="border-t border-gray-100 mt-8">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
          <div className="flex items-center gap-3">
            <span className="font-semibold tracking-[0.16em] uppercase text-gray-700">Authera</span>
            <span className="text-gray-300">·</span>
            <span>Digital Asset Verification</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="mailto:hello@autheraapp.com" className="hover:text-gray-900">hello@autheraapp.com</a>
            <a href="https://app.autheraapp.com" className="hover:text-gray-900">app.autheraapp.com</a>
          </div>
          <div>© 2026 Authera. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
